// src/components/ConsentGate.jsx
//
// Consent access-gate (authguard-consent-gate slice). A signed-in user with NO
// valid consent record for the CURRENT required legal versions must accept
// before entering the app. Catches interrupted-mid-signup sessions + accounts
// orphaned by the #17 signup-consent bug (which recorded no consent).
//
// FAIL-CLOSED but never a hard lock:
//   - checking (docs or consent query in flight) -> FullPageLoader.
//   - a valid consent record for the current terms + privacy versions -> children.
//   - no valid record -> the consent gate (reuses LegalConsent) -> on accept,
//     recordLegalConsent(consentMethod:'reactivation') with the CORRECT
//     `documentVersion` field (#17 fix). A successful record latches the user IN
//     (no re-query, no loop). A failure keeps them on the gate with a friendly,
//     code-free retry (consistent with #17).
//   - legal docs unavailable/incomplete -> a degraded "try again" state (retry
//     re-runs the doc load) -- NOT a permanent lock-out. Because this is checked
//     BEFORE the consent form is shown, LegalConsent (which re-reads the docs)
//     is only ever rendered once the docs have loaded, so its own state stays
//     consistent with this gate.
//
// Reads /legalConsents where uid == me (owner-read, already allowed -- NO rules
// change) and the current versions from useLegalDocs. Does NOT touch AuthProvider
// / the awaited sync barrier. Admins are ordinary users here (consent is
// orthogonal to the admin claim); mock mode has no backend -> gate is bypassed.
import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useLegalDocs } from '../storage/useLegalDocs.js';
import { recordLegalConsent } from '../lib/callables.js';
import LegalConsent from './LegalConsent.jsx';
import FullPageLoader from '../auth/FullPageLoader.jsx';

// The signup requireTypes -- the versions a user must have consented to.
const REQUIRED_TYPES = ['terms_of_service', 'privacy_policy'];

// Session cache of uids already verified/accepted this session. AuthGuard wraps
// each route individually, so ConsentGate remounts on every protected-route
// navigation -- without this, that re-queries + flashes the loader on each hop.
// A uid is added ONLY after a real verification (valid record found, or a
// successful record), so this can never fail OPEN; it is keyed by uid and
// cleared on a full reload (which re-checks -- catching e.g. a terms update).
const SESSION_VERIFIED = new Set();

export default function ConsentGate({ uid, children, hideBlockedUi = false }) {
    const sessionVerified = !!uid && SESSION_VERIFIED.has(uid);
    const { versions, loading: docsLoading, error: docsError, reload: reloadDocs } = useLegalDocs();

    const [consents, setConsents] = useState(null); // null = still loading; array once resolved
    const [queryError, setQueryError] = useState(false);
    const [tick, setTick] = useState(0);            // re-query trigger (retry)
    const [accepted, setAccepted] = useState(false); // one-way latch after a successful record

    // Consent form state (from LegalConsent) + the record action state.
    const [consent, setConsent] = useState({ consented: false, versions: {} });
    const [submitting, setSubmitting] = useState(false);
    const [recordError, setRecordError] = useState(null);

    // Backend disabled (mock / no-db): no consent records to check + no CF to
    // record against -> do not gate (dev-only path).
    const backendOff = AUTH_MODE === 'mock' || !db;

    // ---- load the user's consent records (owner-read list query) ----
    useEffect(() => {
        // Already verified this session (or no backend) -> skip the read.
        if (backendOff || !uid || sessionVerified) { setConsents([]); setQueryError(false); return undefined; }
        let cancelled = false;
        setConsents(null); setQueryError(false);
        getDocs(query(collection(db, 'legalConsents'), where('uid', '==', uid)))
            .then((snap) => {
                if (cancelled) return;
                const rows = [];
                snap.forEach((d) => rows.push(d.data()));
                setConsents(rows);
            })
            .catch((e) => {
                if (cancelled) return;
                console.error('ConsentGate: legalConsents query failed:', e);
                setQueryError(true);
                setConsents([]); // resolved (failed) -> fall to the degraded state, not a spinner
            });
        return () => { cancelled = true; };
    }, [uid, tick, backendOff, sessionVerified]);

    const recheck = useCallback(() => { reloadDocs(); setTick((n) => n + 1); }, [reloadDocs]);

    // Current required versions must all be known to make a valid decision.
    const versionsReady = REQUIRED_TYPES.every((t) => !!versions[t]);
    // Valid consent = a record for EACH required type at its CURRENT version.
    const hasValidConsent = !!consents && versionsReady
        && REQUIRED_TYPES.every((t) => consents.some((c) => c.type === t && c.documentVersion === versions[t]));

    // Cache a genuine verification so remounts (per-route navigation) skip the
    // re-query + loader flash. Only ever set after a REAL pass -> never fails open.
    useEffect(() => {
        if (uid && (accepted || hasValidConsent)) SESSION_VERIFIED.add(uid);
    }, [uid, accepted, hasValidConsent]);

    // ---- decision order (verified/latch first -> can never loop or flash) ----
    if (backendOff || sessionVerified || accepted || hasValidConsent) return children;
    // Optional companions stay absent while blocked. This never renders children.
    if (hideBlockedUi) return null;
    if (docsLoading || consents === null) return <FullPageLoader label="Preparing your account" />;

    // Docs failed / incomplete -> degraded retry (NOT a permanent lock-out). This
    // is BEFORE the consent form, so LegalConsent below only renders once docs load.
    if (docsError || queryError || !versionsReady) {
        return (
            <div className="min-h-screen bg-book-bg paper-texture flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md text-center border border-book-accent/30 bg-book-page/60 p-10 decorative-corner text-book-accent">
                    <div className="font-display text-book-accent text-xs mb-3 font-semibold">One moment</div>
                    <h1 className="font-display text-book-text text-2xl mb-4">We couldn't load the terms</h1>
                    <p className="font-serif italic text-book-muted leading-relaxed mb-8">
                        This is usually temporary. Please try again to continue to your account.
                    </p>
                    <button onClick={recheck}
                        className="px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    // ---- consent gate: no valid record -> require acceptance ----
    const accept = async () => {
        if (!consent.consented || submitting) return;
        setSubmitting(true); setRecordError(null);
        try {
            // Authoritative current versions (this gate's useLegalDocs), field name
            // `documentVersion` per recordLegalConsent (the #17 fix).
            const payload = REQUIRED_TYPES.map((t) => ({ type: t, documentVersion: versions[t] }));
            await recordLegalConsent({ consents: payload, consentMethod: 'reactivation' });
            setAccepted(true); // latch in -> renders children next; cannot loop
        } catch (e) {
            console.error('ConsentGate: recordLegalConsent failed:', e);
            // FAIL-CLOSED: stay on the gate with a friendly, code-free message.
            setRecordError("We couldn't record your agreement. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-book-bg paper-texture flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-lg">
                <div className="text-center mb-8">
                    <div className="ornamental-divider mb-6 text-book-accent">
                        <span className="font-display text-xs font-semibold text-book-muted">Before you continue</span>
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-book-text mb-3">One quick confirmation</h1>
                    <p className="font-serif italic text-book-muted leading-relaxed">
                        Please confirm you agree to our terms to continue to your account.
                    </p>
                </div>

                <LegalConsent theme="gold"
                    requireTypes={REQUIRED_TYPES}
                    onConsentChange={setConsent} />

                {recordError && (
                    <p className="mt-4 font-serif italic text-red-300 text-sm text-center">{recordError}</p>
                )}

                <button onClick={accept} disabled={!consent.consented || submitting}
                    className="w-full mt-6 py-3 bg-book-accent text-black font-display text-sm hover:bg-book-accentLight transition-colors btn-sweep disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-book-muted">
                    {submitting ? 'Confirming…' : 'Agree and continue'}
                </button>
            </div>
        </div>
    );
}
